'use client';

import cn from 'classnames';
import Link from 'next/link';
import { forwardRef, Ref } from 'react';
import { ButtonProps } from '../model/button.types';
import styles from './button.module.scss';


export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button (props, ref) {
    const {
        variant = 'primary',
        size    = 'md',
        children,
    } = props;

    const hasBoxSize    = variant !== 'link' && variant !== 'naked';
    const hasLoadingBar = props.loading && variant !== 'link' && variant !== 'naked';

    const commonClassName = cn(
        styles.root,
        styles[variant],
        hasBoxSize && styles[size],
        props.fullWidth && styles.fullWidth,
        hasLoadingBar && styles.loading,
        props.className,
    );

    if (props.as === 'a') {
        // eslint-disable-next-line  @typescript-eslint/no-unused-vars
        const { as, href, target, rel, onClick, leftIcon, rightIcon, className, fullWidth, ...anchorProps } = props;

        const safeRel = target === '_blank'
            ? [ rel, 'noopener', 'noreferrer' ].filter(Boolean).join(' ')
            : rel;

        const isDisabled = Boolean(props.loading);

        if (isDisabled) {
            return (
                <span
                    className={ commonClassName }
                    aria-disabled="true"
                    data-loading={ hasLoadingBar ? 'true' : undefined }
                >
                    {
                        leftIcon &&
                        <span className={ cn(styles.icon, styles.leftIcon) } aria-hidden="true">
                            { leftIcon }
                        </span>
                    }

                    <span className={ styles.label }>
                        { children }
                    </span>

                    {
                        rightIcon &&
                        <span className={ cn(styles.icon, styles.rightIcon) } aria-hidden="true">
                            { rightIcon }
                        </span>
                    }
                </span>
            );
        }

        return (
            <Link
                ref={ ref as Ref<HTMLAnchorElement> }
                className={ commonClassName }
                href={ href }
                target={ target }
                rel={ safeRel }
                aria-disabled={ isDisabled || undefined }
                data-loading={ hasLoadingBar ? 'true' : undefined }
                onClick={ (event) => {
                    if (isDisabled) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                    }

                    onClick?.(event);
                } }
                { ...anchorProps }
            >
                {
                    leftIcon &&
                    <span className={ cn(styles.icon, styles.leftIcon) } aria-hidden="true">
                        { leftIcon }
                    </span>
                }

                <span className={ styles.label }>
                    { children }
                </span>

                {
                    rightIcon &&
                    <span className={ cn(styles.icon, styles.rightIcon) } aria-hidden="true">
                        { rightIcon }
                    </span>
                }
            </Link>
        );
    }

    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    const { as, disabled, type = 'button', leftIcon, rightIcon, loading, fullWidth, className, ...buttonProps } = props;

    const isDisabled = Boolean(disabled || loading);

    return (
        <button
            ref={ ref as Ref<HTMLButtonElement> }
            type={ type }
            className={ commonClassName }
            disabled={ isDisabled }
            data-loading={ hasLoadingBar ? 'true' : undefined }
            { ...buttonProps }
        >
            {
                leftIcon &&
                <span className={ cn(styles.icon, styles.leftIcon) } aria-hidden="true">
                    { leftIcon }
                </span>
            }

            <span className={ styles.label }>
                { children }
            </span>

            {
                rightIcon &&
                <span className={ cn(styles.icon, styles.rightIcon) } aria-hidden="true">
                    { rightIcon }
                </span>
            }
        </button>
    );
});
